/**
 * File: assets/basari-prescription-upload-form.js
 * Purpose: Upload Prescription UI — presentation + Controller wiring only.
 * Sprint: 1B.13 Phase B — Upload Prescription UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-prescription.js (Controller upload API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.uploadPrescription
 *   - All updates go through PrescriptionController upload methods
 *   - No business / file validation (render controller state only)
 *   - Drag-and-drop + browse delegate to selectUploadFile / replaceUploadFile
 */
(function (global) {
  'use strict';

  var BasariPrescriptionUploadForm = global.BasariPrescriptionUploadForm || {};
  var READY_ATTR = 'data-basari-rx-upload-form-ready';

  var STATUS = {
    IDLE: 'idle',
    SELECTING: 'selecting',
    VALIDATING: 'validating',
    READY: 'ready',
    SUBMITTING: 'submitting',
    COMPLETED: 'completed',
    ERROR: 'error'
  };

  function getUploadStatusMap() {
    if (global.BasariPrescription && global.BasariPrescription.UPLOAD_STATUS) {
      return global.BasariPrescription.UPLOAD_STATUS;
    }
    return STATUS;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  /**
   * File-row visibility — driven only by presentation state from the controller.
   * Empty: idle / no filename. Selected: ready (or later statuses) with a filename.
   */
  function shouldShowFileRow(upload) {
    if (!upload) return false;
    var status = upload.uploadStatus || STATUS.IDLE;
    var map = getUploadStatusMap();
    var fileName = upload.fileName ? String(upload.fileName).trim() : '';

    if (!fileName) return false;

    return (
      status === (map.READY || STATUS.READY) ||
      status === (map.SUBMITTING || STATUS.SUBMITTING) ||
      status === (map.COMPLETED || STATUS.COMPLETED) ||
      status === (map.ERROR || STATUS.ERROR) ||
      status === (map.SELECTING || STATUS.SELECTING) ||
      status === (map.VALIDATING || STATUS.VALIDATING)
    );
  }

  /**
   * @param {HTMLElement} root - [data-basari-prescription-root][data-basari-prescription-source="upload"]
   * @param {Object} options
   * @param {Function} options.getController
   * @param {Function} [options.onClose]
   * @param {Function} [options.onConfirm]
   * @param {HTMLElement} [options.eventTarget]
   */
  function UploadPrescriptionUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.form = null;
    this.cache = {};
    this.bound = false;
    this._syncing = false;
    this._dragDepth = 0;
    this._onHostEvent = this.onHostEvent.bind(this);
    this._onFormEvent = this.onFormEvent.bind(this);
    this._onDragEnter = this.onDragEnter.bind(this);
    this._onDragLeave = this.onDragLeave.bind(this);
    this._onDragOver = this.onDragOver.bind(this);
    this._onDrop = this.onDrop.bind(this);
  }

  UploadPrescriptionUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return null;
  };

  UploadPrescriptionUI.prototype.cacheSelectors = function () {
    var form = this.root.querySelector('[data-basari-rx-form]');
    this.form = form;
    this.cache.form = form;
    this.cache.upload = form ? form.querySelector('[data-basari-rx-upload]') : null;
    this.cache.dropzone = form ? form.querySelector('[data-basari-rx-upload-dropzone]') : null;
    this.cache.empty = form ? form.querySelector('[data-basari-rx-upload-empty]') : null;
    this.cache.fileRow = form ? form.querySelector('[data-basari-rx-upload-file]') : null;
    this.cache.filename = form ? form.querySelector('[data-basari-rx-upload-filename]') : null;
    this.cache.progress = form ? form.querySelector('[data-basari-rx-upload-progress]') : null;
    this.cache.progressBar = form ? form.querySelector('[data-basari-rx-upload-progress-bar]') : null;
    this.cache.statusText = form ? form.querySelector('[data-basari-rx-upload-status-text]') : null;
    this.cache.live = form ? form.querySelector('[data-basari-rx-upload-live]') : null;
    this.cache.fileInput = form ? form.querySelector('[data-basari-rx-upload-input]') : null;
    this.cache.browseBtn = form ? form.querySelector('[data-basari-rx-upload-browse]') : null;
    this.cache.replaceBtn = form ? form.querySelector('[data-basari-rx-upload-replace]') : null;
    this.cache.removeBtn = form ? form.querySelector('[data-basari-rx-upload-remove]') : null;
    this.cache.notes = form ? form.querySelector('[data-basari-rx-field="notes"]') : null;
    this.cache.charCurrent = form ? form.querySelector('[data-basari-rx-char-current]') : null;
    this.cache.confirmBtn = form ? form.querySelector('[data-basari-rx-confirm]') : null;
    this.cache.closeBtn = form ? form.querySelector('[data-basari-rx-close]') : null;
  };

  UploadPrescriptionUI.prototype.mount = function () {
    if (!this.root || this.root.getAttribute(READY_ATTR) === 'true') {
      this.hydrateFromController();
      return this;
    }

    this.cacheSelectors();
    if (!this.form) return this;

    this.bindEvents();
    this.hydrateFromController();
    this.root.setAttribute(READY_ATTR, 'true');
    return this;
  };

  UploadPrescriptionUI.prototype.bindEvents = function () {
    if (this.bound || !this.form) return;

    this.form.addEventListener('input', this._onFormEvent);
    this.form.addEventListener('change', this._onFormEvent);
    this.form.addEventListener('click', this._onFormEvent);
    this.form.addEventListener('keydown', this._onFormEvent);
    this.form.addEventListener('submit', this._onFormEvent);

    if (this.cache.dropzone) {
      this.cache.dropzone.addEventListener('dragenter', this._onDragEnter);
      this.cache.dropzone.addEventListener('dragleave', this._onDragLeave);
      this.cache.dropzone.addEventListener('dragover', this._onDragOver);
      this.cache.dropzone.addEventListener('drop', this._onDrop);
    }

    var eventTarget = this.options.eventTarget || this.root;
    if (eventTarget && eventTarget.addEventListener) {
      eventTarget.addEventListener('basari:prescription:upload:initialized', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:updated', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:file-selected', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:file-removed', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:validation-failed', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:ready', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:submission-prepared', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:upload:reset', this._onHostEvent);
    }

    this.bound = true;
  };

  UploadPrescriptionUI.prototype.onHostEvent = function (event) {
    if (!event || this._syncing) return;
    var detail = event.detail || {};
    var upload = detail.upload || null;

    if (event.type === 'basari:prescription:upload:validation-failed') {
      this.hydrateFromUpload(upload);
      this.applyValidationResult(detail.result || (upload && upload.validation) || {});
      return;
    }

    this.hydrateFromUpload(upload);
  };

  UploadPrescriptionUI.prototype.onFormEvent = function (event) {
    if (!event || this._syncing) return;
    var target = event.target;
    if (!target) return;

    if (event.type === 'submit') {
      event.preventDefault();
      this.handleConfirm();
      return;
    }

    if (event.type === 'keydown') {
      if (
        target.matches('[data-basari-rx-upload-dropzone]') &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        this.openFilePicker(false);
      }
      return;
    }

    if (event.type === 'click') {
      if (target.closest('[data-basari-rx-confirm]')) {
        event.preventDefault();
        this.handleConfirm();
        return;
      }
      if (target.closest('[data-basari-rx-close]')) {
        event.preventDefault();
        this.handleClose();
        return;
      }
      if (target.closest('[data-basari-rx-upload-browse]')) {
        event.preventDefault();
        this.openFilePicker(false);
        return;
      }
      if (target.closest('[data-basari-rx-upload-replace]')) {
        event.preventDefault();
        this.openFilePicker(true);
        return;
      }
      if (target.closest('[data-basari-rx-upload-remove]')) {
        event.preventDefault();
        this.handleRemove();
        return;
      }
      if (target.closest('[data-basari-rx-upload-dropzone]')) {
        event.preventDefault();
        this.openFilePicker(false);
        return;
      }
      return;
    }

    if (event.type === 'change' && target.matches('[data-basari-rx-upload-input]')) {
      this.handleFileInputChange(target);
      return;
    }

    if (target.matches('[data-basari-rx-field="notes"]')) {
      this.updateCharCount(target);
      this.callController('setUploadAdditionalInformation', target.value);
      return;
    }

    if (target.matches('[data-basari-rx-field="patientName"]')) {
      this.callController('setUploadPatientName', target.value);
      return;
    }

    if (target.matches('[data-basari-rx-field="dob"]')) {
      this.callController('setUploadDOB', target.value);
      return;
    }

    if (target.matches('[data-basari-rx-field="gender"]')) {
      this.callController('setUploadGender', target.value);
    }
  };

  UploadPrescriptionUI.prototype.callController = function (methodName, arg) {
    var controller = this.getController();
    if (!controller || typeof controller[methodName] !== 'function') return null;
    this._syncing = true;
    var result = null;
    try {
      result = controller[methodName](arg);
    } finally {
      this._syncing = false;
    }
    return result;
  };

  UploadPrescriptionUI.prototype.openFilePicker = function (isReplace) {
    var input = this.cache.fileInput;
    if (!input) return;
    input.setAttribute('data-basari-rx-upload-mode', isReplace ? 'replace' : 'select');
    if (typeof input.click === 'function') {
      input.click();
    }
  };

  UploadPrescriptionUI.prototype.handleFileInputChange = function (input) {
    if (!input) return;
    var mode = input.getAttribute('data-basari-rx-upload-mode') || 'select';
    var method = mode === 'replace' ? 'replaceUploadFile' : 'selectUploadFile';
    this.clearFileValidation();
    var result = this.callController(method, input);
    if (result) {
      this.hydrateFromUpload(result);
      if (result.validation && !result.validation.valid) {
        this.applyValidationResult(result.validation);
      }
    }
    /* Allow selecting the same file again after remove/replace. */
    input.value = '';
    input.removeAttribute('data-basari-rx-upload-mode');
  };

  UploadPrescriptionUI.prototype.handleRemove = function () {
    this.clearFileValidation();
    var result = this.callController('removeUploadFile');
    if (result) this.hydrateFromUpload(result);
  };

  UploadPrescriptionUI.prototype.onDragEnter = function (event) {
    event.preventDefault();
    event.stopPropagation();
    this._dragDepth += 1;
    this.setDropActive(true);
  };

  UploadPrescriptionUI.prototype.onDragLeave = function (event) {
    event.preventDefault();
    event.stopPropagation();
    this._dragDepth = Math.max(0, this._dragDepth - 1);
    if (this._dragDepth === 0) {
      this.setDropActive(false);
    }
  };

  UploadPrescriptionUI.prototype.onDragOver = function (event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.setDropActive(true);
  };

  UploadPrescriptionUI.prototype.onDrop = function (event) {
    event.preventDefault();
    event.stopPropagation();
    this._dragDepth = 0;
    this.setDropActive(false);

    var files = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files : null;
    if (!files || !files.length) return;

    this.clearFileValidation();
    var result = this.callController('selectUploadFile', files);
    if (result) {
      this.hydrateFromUpload(result);
      if (result.validation && !result.validation.valid) {
        this.applyValidationResult(result.validation);
      }
    }
  };

  UploadPrescriptionUI.prototype.setDropActive = function (active) {
    if (!this.cache.dropzone) return;
    if (active) {
      this.cache.dropzone.classList.add('is-dragover');
      this.cache.dropzone.setAttribute('data-basari-rx-upload-dragover', 'true');
    } else {
      this.cache.dropzone.classList.remove('is-dragover');
      this.cache.dropzone.removeAttribute('data-basari-rx-upload-dragover');
    }
  };

  UploadPrescriptionUI.prototype.updateCharCount = function (textarea) {
    if (!textarea || !this.cache.charCurrent) return;
    this.cache.charCurrent.textContent = String((textarea.value || '').length);
  };

  UploadPrescriptionUI.prototype.hydrateFromController = function () {
    var controller = this.getController();
    if (!controller || typeof controller.getUploadPrescription !== 'function') return;
    this.hydrateFromUpload(controller.getUploadPrescription());
  };

  UploadPrescriptionUI.prototype.hydrateFromUpload = function (upload) {
    if (!this.form || !upload) return;

    this._syncing = true;
    try {
      this.setFieldValue('patientName', upload.patientName);
      this.setFieldValue('notes', upload.additionalInformation);
      this.setFieldValue('dob', upload.dob);
      this.setFieldValue('gender', upload.gender);

      if (this.cache.notes) {
        this.updateCharCount(this.cache.notes);
      }

      this.renderUploadState(upload);

      if (upload.validation && upload.validation.errors) {
        this.applyValidationErrors(upload.validation.errors, upload.validation.message);
      } else {
        this.clearFileValidation();
      }
    } finally {
      this._syncing = false;
    }
  };

  UploadPrescriptionUI.prototype.setFieldValue = function (key, value) {
    var el = this.form.querySelector('[data-basari-rx-field="' + key + '"]');
    if (!el) return;
    var next = value == null ? '' : String(value);
    if (el.value !== next) el.value = next;
  };

  UploadPrescriptionUI.prototype.renderUploadState = function (upload) {
    var status = (upload && upload.uploadStatus) || STATUS.IDLE;
    var showFile = shouldShowFileRow(upload);

    if (this.cache.upload) {
      this.cache.upload.setAttribute('data-basari-rx-upload-status', status);
      this.cache.upload.setAttribute('data-basari-rx-upload-mode', showFile ? 'file' : 'empty');
    }

    /* Empty ↔ file selected are mutually exclusive. */
    setHidden(this.cache.empty, showFile);
    setHidden(this.cache.dropzone, showFile);
    setHidden(this.cache.fileRow, !showFile);
    setHidden(this.cache.browseBtn, showFile);
    setHidden(this.cache.replaceBtn, !showFile);

    if (this.cache.filename) {
      this.cache.filename.textContent = (upload && upload.fileName) || '';
    }

    var progress = Number(upload && upload.uploadProgress) || 0;
    if (this.cache.progress) {
      this.cache.progress.setAttribute('aria-valuenow', String(progress));
      this.cache.progress.setAttribute('aria-hidden', showFile ? 'false' : 'true');
      this.cache.progress.classList.remove('is-active', 'is-complete');
      if (progress >= 100) {
        this.cache.progress.classList.add('is-complete');
      } else if (progress > 0) {
        this.cache.progress.classList.add('is-active');
      }
    }

    var statusLabel = this.statusLabel(status, upload);
    if (this.cache.statusText) {
      this.cache.statusText.textContent = statusLabel;
    }
    if (this.cache.live) {
      this.cache.live.textContent = statusLabel;
    }

    if (this.cache.dropzone) {
      this.cache.dropzone.setAttribute('aria-disabled', showFile ? 'true' : 'false');
      this.cache.dropzone.setAttribute('tabindex', showFile ? '-1' : '0');
    }
  };

  UploadPrescriptionUI.prototype.statusLabel = function (status, upload) {
    var map = getUploadStatusMap();
    if (status === (map.SELECTING || STATUS.SELECTING)) return 'Selecting file';
    if (status === (map.VALIDATING || STATUS.VALIDATING)) return 'Validating file';
    if (status === (map.READY || STATUS.READY)) return 'File ready';
    if (status === (map.SUBMITTING || STATUS.SUBMITTING)) return 'Preparing submission';
    if (status === (map.COMPLETED || STATUS.COMPLETED)) return 'Upload completed';
    if (status === (map.ERROR || STATUS.ERROR)) {
      return (upload && upload.validation && upload.validation.message) || 'Upload error';
    }
    return 'No file selected';
  };

  UploadPrescriptionUI.prototype.clearFileValidation = function () {
    if (!this.form) return;
    var keys = ['file', 'fileSize', 'mimeType'];
    var i;
    for (i = 0; i < keys.length; i += 1) {
      var errorEl = this.form.querySelector('[data-basari-rx-error="' + keys[i] + '"]');
      if (!errorEl) continue;
      errorEl.textContent = '';
      setHidden(errorEl, true);
    }
    if (this.cache.upload) {
      this.cache.upload.removeAttribute('data-basari-rx-upload-invalid');
    }
  };

  UploadPrescriptionUI.prototype.clearValidation = function () {
    if (!this.form) return;
    var errors = this.form.querySelectorAll('[data-basari-rx-error]');
    var invalids = this.form.querySelectorAll('[aria-invalid="true"]');
    var i;
    for (i = 0; i < errors.length; i += 1) {
      errors[i].textContent = '';
      setHidden(errors[i], true);
    }
    for (i = 0; i < invalids.length; i += 1) {
      invalids[i].setAttribute('aria-invalid', 'false');
    }
    if (this.cache.upload) {
      this.cache.upload.removeAttribute('data-basari-rx-upload-invalid');
    }
  };

  UploadPrescriptionUI.prototype.applyValidationResult = function (result) {
    this.clearFileValidation();
    if (!result) return;
    var errors = result.errors || {};
    var message = result.message || '';
    this.applyValidationErrors(errors, message);
  };

  UploadPrescriptionUI.prototype.applyValidationErrors = function (errors, fallbackMessage) {
    if (!this.form) return;
    var key;
    var shown = false;

    for (key in errors) {
      if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
      var code = errors[key];
      if (code == null || code === '') continue;

      var errorEl = this.form.querySelector('[data-basari-rx-error="' + key + '"]');
      if (!errorEl && (key === 'file' || key === 'fileSize' || key === 'mimeType')) {
        errorEl = this.form.querySelector('[data-basari-rx-error="file"]');
      }
      if (!errorEl && key === 'additionalInformation') {
        errorEl = this.form.querySelector('[data-basari-rx-error="notes"]');
      }
      if (!errorEl) continue;

      var text = fallbackMessage && !shown ? String(fallbackMessage) : String(code);
      if (fallbackMessage && (key === 'file' || key === 'fileSize' || key === 'mimeType')) {
        text = String(fallbackMessage);
      }
      errorEl.textContent = text;
      setHidden(errorEl, false);
      shown = true;

      var control = this.findControlForErrorKey(key);
      if (control) control.setAttribute('aria-invalid', 'true');
    }

    if (!shown && fallbackMessage) {
      var fileError = this.form.querySelector('[data-basari-rx-error="file"]');
      if (fileError) {
        fileError.textContent = String(fallbackMessage);
        setHidden(fileError, false);
      }
    }

    if (this.cache.upload && (errors.file || errors.fileSize || errors.mimeType || fallbackMessage)) {
      this.cache.upload.setAttribute('data-basari-rx-upload-invalid', 'true');
    }
  };

  UploadPrescriptionUI.prototype.findControlForErrorKey = function (key) {
    if (!this.form || !key) return null;
    if (key === 'file' || key === 'fileSize' || key === 'mimeType') {
      return this.cache.fileInput || this.cache.dropzone;
    }
    if (key === 'additionalInformation') {
      return this.form.querySelector('[data-basari-rx-field="notes"]');
    }
    return this.form.querySelector('[data-basari-rx-field="' + key + '"]');
  };

  UploadPrescriptionUI.prototype.handleConfirm = function () {
    var controller = this.getController();
    var result = null;
    if (controller && typeof controller.prepareUploadSubmission === 'function') {
      this._syncing = true;
      try {
        result = controller.prepareUploadSubmission();
      } finally {
        this._syncing = false;
      }
      if (result) {
        this.hydrateFromUpload(result);
        if (result.validation && !result.validation.valid) {
          this.applyValidationResult(result.validation);
        }
      }
    }
    if (typeof this.options.onConfirm === 'function') {
      this.options.onConfirm(result);
    }
  };

  UploadPrescriptionUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
    }
  };

  UploadPrescriptionUI.prototype.refresh = function () {
    this.hydrateFromController();
  };

  function mount(root, options) {
    if (!root) return null;
    var ui = new UploadPrescriptionUI(root, options || {});
    return ui.mount();
  }

  BasariPrescriptionUploadForm.UploadPrescriptionUI = UploadPrescriptionUI;
  BasariPrescriptionUploadForm.mount = mount;

  global.BasariPrescriptionUploadForm = BasariPrescriptionUploadForm;
})(typeof window !== 'undefined' ? window : this);
